import { useEffect } from 'react';

import { subscribeWindowClass } from '../../hooks/useWindowClass';

/**
 * THE SHELL OWNS THE PIN (docs/platform-standards.md §13.2). On desktop the
 * whole header stays at the top of the window while the page scrolls; on a
 * phone nothing is pinned. Anything else that has to sit beneath the header, or
 * land beneath it, needs to know how tall it is, so while the header really is
 * pinned this publishes that height on <html>:
 *
 *   --shell-pin-offset     the header's height: where a second sticky thing
 *                          (the list's detail pane) starts
 *   --shell-pin-clearance  the height plus a small gap: the document's
 *                          `scroll-padding-top`, so an anchor, a
 *                          `scrollIntoView({ block: 'start' })` or a focused
 *                          field lands beneath the header and not behind it
 *
 * "Really pinned" is read from the computed position, not guessed from a
 * breakpoint: the stylesheet decides (sticky in medium and expanded, static in
 * compact, platform-standards §18) and
 * this follows it, so the two cannot disagree. Both variables are removed on a
 * phone and on unmount, which leaves every route outside the shell on the
 * fallbacks its own CSS carries.
 */
export const PIN_OFFSET_VAR = '--shell-pin-offset';
export const PIN_CLEARANCE_VAR = '--shell-pin-clearance';
const CLEARANCE_GAP = 8;

export default function useShellPin(ref) {
	useEffect(() => {
		const header = ref.current;
		if (!header || typeof window === 'undefined') return undefined;
		const root = document.documentElement;

		const clear = () => {
			root.style.removeProperty(PIN_OFFSET_VAR);
			root.style.removeProperty(PIN_CLEARANCE_VAR);
		};
		const publish = () => {
			if (window.getComputedStyle(header).position !== 'sticky') {
				clear();
				return;
			}
			const height = Math.round(header.getBoundingClientRect().height);
			root.style.setProperty(PIN_OFFSET_VAR, `${height}px`);
			root.style.setProperty(PIN_CLEARANCE_VAR, `${height + CLEARANCE_GAP}px`);
		};

		publish();
		// The height changes with its content (an error line, wrapped actions); the
		// pin itself changes when the window crosses into or out of compact.
		window.addEventListener('resize', publish);
		const stopClass = subscribeWindowClass(publish);
		const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
		observer?.observe(header);

		return () => {
			window.removeEventListener('resize', publish);
			stopClass();
			observer?.disconnect();
			clear();
		};
	}, [ref]);
}
